import React from 'react';
import gql from 'graphql-tag';
import I18n from 'i18n-js';

import { SimilarListingsFields } from '../../schemas/rql';

import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { OverflowingRow } from '@reverbdotcom/commons';
import { AddToCartButtonFragment } from '@reverbdotcom/commons/src/components/add_to_cart_button';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import ListingCard, { ListingCardFragment } from '@reverbdotcom/commons/src/components/listing_card';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { isReferredFromGoogle } from '@reverbdotcom/commons/src/url_helpers';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { MParticleEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';

interface IProps {
  similarListings: SimilarListingsFields.Fragment[];
  tilesFourWideMax?: boolean;
  largeTiles?: boolean;
  title?: string;
  showFlags: boolean;
  eventName: MParticleEventName.ViewedItemEndedHeaderSimilarListings
  | MParticleEventName.ViewedItemFooterSimilarListings
  | MParticleEventName.ViewedItemPaidHeaderSimilarListings;
  experiments?: string[];
  displayAddToCart?: boolean;
}

const COMPONENT_NAME = 'SimilarListings';

export function SimilarListingsRow(props: IProps) {
  const user = useUser();

  const event: MParticleEvent = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    similarListingsCount: props.similarListings?.length || 0,
  };

  if (props.experiments) {
    event.experiments = formatExperimentsForEventAttributes(user, props.experiments);
  }

  useViewTracking(event, true);

  const { title = I18n.t('discovery.listingsRow.similarListings') } = props;

  const shouldRender = !!props.similarListings?.length;

  const displayAddToCart = isReferredFromGoogle(useURL()) || !!props.displayAddToCart;

  if (!shouldRender) { return null; }

  return (
    <div className="similar-listings-row">
      <OverflowingRow
        title={title}
      >
        <Tiles
          placeholder={<ListingCard />}
          singleRow
          fourWideMax={props.tilesFourWideMax}
          largeTiles={props.largeTiles}
          loading={!props.similarListings}
        >
          {props.similarListings.map((listing, idx) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              position={idx}
              displayAddToCart={displayAddToCart}
              trackingName={COMPONENT_NAME}
              showFlag={props.showFlags}
            />
          ))}
        </Tiles>
      </OverflowingRow>
    </div>
  );
}

export const SimilarListingsFragment = gql`
  fragment SimilarListingsFields on Listing {
    _id
    id
    ...ListingCardFields
    ...AddToCartButtonData
  }
  ${ListingCardFragment}
  ${AddToCartButtonFragment}
`;

export default SimilarListingsRow;
